import { CanvasBoardAri } from '@atlassian/ari/canvas/board';
import { ConfluenceSpaceAri } from '@atlassian/ari/confluence/space';

import { type RawFrameToken } from '@canvas/frame-api-types';
import { PaywallFeature } from '@canvas/paywall';
import { unlockAllPaywallsResponseMock } from '@canvas/paywall-test-utils';

import { fetchBoards } from './fetchers';

export function getTokenFromLocalServer({
  boardId,
  localSpaceId,
  localAsapToken,
  developmentCloudId,
}: {
  boardId: string;
  localSpaceId: string;
  localAsapToken: string;
  developmentCloudId: string;
}): Promise<RawFrameToken> {
  const containerAri = ConfluenceSpaceAri.create({
    spaceId: localSpaceId,
    siteId: developmentCloudId,
  });
  const boardAri = CanvasBoardAri.create({
    siteId: developmentCloudId,
    boardId,
  });

  return Promise.resolve({
    container: containerAri.toString(),
    board: boardAri.toString(),
    accessToken: localAsapToken,
    mediaToken: 'media-token',
    mediaBaseUrl: 'media-baseurl',
    mediaCollectionId: 'media-collection',
    features: unlockAllPaywallsResponseMock,
    // We return a token with valid Data Security Policy for local development
    policies: { dataSecurityPolicy: { pageExport: true } },
  });
}

async function fetchTokensViaContentId(
  developmentSpaceId: string,
  developmentCloudId: string,
  contentId: string
) {
  const origin = new URL(window.location.href).origin;
  const url = new URL(`${origin}/gateway/api/ex/confluence/${developmentCloudId}`);
  url.pathname += `/wiki/rest/internal/1.0/canvas/session/${developmentSpaceId}`;
  url.searchParams.set('contentId', contentId);

  // deepcode ignore Ssrf: Local devloop only
  const response = await fetch(url, {
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
  });
  return response;
}

async function fetchJsonFromSpaceIdAndCloudId(
  developmentSpaceId: string,
  developmentCloudId: string,
  boardId: string
) {
  const origin = new URL(window.location.href).origin;
  const url = new URL(`${origin}/gateway/api/ex/confluence/${developmentCloudId}`);
  url.pathname += `/wiki/rest/internal/1.0/canvas/session/${developmentSpaceId}`;
  url.searchParams.set('whiteboardId', boardId);

  // deepcode ignore Ssrf: Local devloop only
  const response = await fetch(url, {
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
  });
  return response;
}

/** // TODO: re-instate board lookup backup, as followup PR/when necessary */
// async function findValidBoardWithRetries(
//   developmentSpaceId: string,
//   developmentCloudId: string
// ) {
//   // Attempt
//   let safeBoardId: string | null = null;

//   // TODO-who - drill `conatiner Key`
//   const containerKey = process.env.VITE_CANVAS_EAP_CONTAINER_KEY || null;
//   if (!containerKey) {
//     console.warn(`VITE_CANVAS_EAP_CONTAINER_KEY does not exist?`);
//     return;
//   }
//   // This can be abstracted out to a common board data situation, but
//   // re-fetching it here in case of staleness
//   const boardData = await fetchBoards(containerKey);

//   if (!boardData) {
//     throw new Error('board data is not in the shape we expect');
//   }

//   /** If there is  more than 10 cooked boards, something is very wrong. */
//   const ids = boardData.filter((_, i) => i < 10).map((node) => node.referenceId);

//   let validTokenData = null;
//   for (const boardId of ids) {
//     try {
//       const response = await fetchJsonFromSpaceIdAndCloudId(
//         developmentSpaceId,
//         developmentCloudId,
//         boardId
//       );
//       if (response.status === 200) {
//         const data = await response.json();
//         validTokenData = data;
//         safeBoardId = boardId;
//         break;
//       }
//     } catch (error) {
//       console.error(`Error fetching data for board ID ${boardId}:`, error);
//     }
//   }
//   return {
//     boardId: safeBoardId,
//     validTokenData: validTokenData,
//   };
// }

async function rerouteTokenRequestToContentId(
  developmentSpaceId: string,
  developmentCloudId: string,
  boardId: string
) {
  const containerKey = process.env.VITE_CANVAS_EAP_CONTAINER_KEY || null;
  if (!containerKey) {
    console.warn(`VITE_CANVAS_EAP_CONTAINER_KEY does not exist?`);
    return;
  }

  const boardData = await fetchBoards(containerKey);

  if (!boardData) {
    throw new Error('board data is not in the shape we expect');
  }

  /** If there is  more than 10 cooked boards, something is very wrong. */
  const boardForReferenceId = boardData.find((node) => node.referenceId === boardId)?.id;
  if (!boardForReferenceId) {
    throw new Error('no boardForReferenceId found!');
  }

  const response = await fetchTokensViaContentId(
    developmentSpaceId,
    developmentCloudId,
    boardForReferenceId
  );
  return response;
}

/**
 * `getTokenFromSite()`:
 *
 * 1. First, try get a token from try it with given boardId.
 *
 * If this fails, we utilise findValidBoardWithRetries to get a safe board,
 *
 * Then suggest to the dev that they click out to that "safe for
 * `/canvas/session`" board
 *
 */
export async function getTokenFromSite({
  boardId,
  developmentSpaceId,
  developmentCloudId,
}: {
  boardId: string;
  developmentSpaceId: string;
  developmentCloudId: string;
}): Promise<RawFrameToken> {
  // deepcode ignore Ssrf: Local devloop only
  const response = await fetchJsonFromSpaceIdAndCloudId(
    developmentSpaceId,
    developmentCloudId,
    boardId
  );

  const backupResponse =
    response.status === 404
      ? await rerouteTokenRequestToContentId(
          developmentSpaceId,
          developmentCloudId,
          boardId
        )
      : undefined;

  const responseToUse = backupResponse ?? response;
  const json = await responseToUse.json();

  const containerAri = ConfluenceSpaceAri.create({
    spaceId: developmentSpaceId,
    siteId: developmentCloudId,
  });
  const boardAri = CanvasBoardAri.create({
    siteId: developmentCloudId,
    boardId,
  });

  // inject shimmed features which are not stored as entitlements yet
  const features: RawFrameToken['features'] = json.features ?? { whiteboards: {} };

  if (features?.whiteboards) {
    features.whiteboards[PaywallFeature.AI_GENERATE] = { isEntitled: true };
    features.whiteboards[PaywallFeature.AI_CLUSTERING] = { isEntitled: true };
    features.whiteboards[PaywallFeature.AI_TEAMMATE] = { isEntitled: true };
    features.whiteboards[PaywallFeature.AI_SMART_BUTTON] = { isEntitled: true };
    features.whiteboards[PaywallFeature.AI_JIRA_SEARCH] = { isEntitled: true };
  }

  return {
    container: containerAri.toString(),
    board: boardAri.toString(),
    accessToken: json.access.token,
    mediaToken: json.media.token,
    mediaBaseUrl: json.media.baseUrl,
    mediaCollectionId: json.media.contentId,
    features: json.features,
    // We return a token with valid Data Security Policy for local development
    policies: { dataSecurityPolicy: { pageExport: true } },
  };
}
